/**
 * Chiamate alle API del servizio `secrets`: credenziali dei set.
 *
 * Una regola attraversa tutto il file, ed è la ragione per cui esiste un client
 * separato da `agents.ts`: **il valore di una credenziale va solo in una
 * direzione**. Si invia con `putSecret` e non torna da nessuna rotta. Il tipo
 * `SecretMetadata` non ha un campo in cui potrebbe finire, e un test di
 * contratto lo verifica: ciò che il tipo non dichiara, il codice non può
 * leggere per distrazione.
 *
 * Dal 2026-09-26 (ADR 0008 §9) una credenziale ha un **proprietario** generico:
 * la credenziale di un harness appartiene al set (`owner_type: agent_set`,
 * `purpose: harness:<harness>`), non più al progetto.
 *
 * Provvisorio dichiarato: tipi scritti a mano, da generare dagli schemi OpenAPI
 * (CLAUDE.md §2.3).
 */

import type { CredentialKind, HarnessId } from './agentTypes';
import type { ApiClient } from './client';

/** Tipo di proprietario di una credenziale scrivibile dal browser. */
export type SecretOwnerType = 'project' | 'agent_set';

/**
 * Metadati di una credenziale. **Non contiene il valore**, per scelta: il
 * backend non lo restituisce e questo tipo non ha un posto dove metterlo.
 */
export interface SecretMetadata {
  /** Identificativo della credenziale. */
  id: string;
  /** Tipo di proprietario. */
  owner_type: SecretOwnerType;
  /** Proprietario (id del set o del progetto). */
  owner_id: string;
  /** Scopo, es. `harness:claude-code`. */
  purpose: string;
  /** Natura della credenziale. */
  kind: CredentialKind;
  /** Istante del primo inserimento, ISO 8601. */
  created_at: string;
  /** Istante dell'ultima sostituzione, ISO 8601, `null` se mai sostituita. */
  rotated_at: string | null;
}

/** Dati per inserire o sostituire una credenziale. */
export interface NewSecret {
  /** Tipo di proprietario. */
  owner_type: SecretOwnerType;
  /** Proprietario. */
  owner_id: string;
  /** Scopo della credenziale. */
  purpose: string;
  /** Natura della credenziale. */
  kind: CredentialKind;
  /** Valore in chiaro. Viaggia una volta sola e non torna indietro. */
  value: string;
}

/**
 * Scopo con cui si salva la credenziale di un harness (ADR 0008 §9).
 *
 * @param harness Harness a cui la credenziale serve.
 * @returns Lo scopo, es. `harness:claude-code`.
 */
export function harnessPurpose(harness: HarnessId): string {
  return `harness:${harness}`;
}

/** Operazioni sulle credenziali, esposte alla UI. */
export class SecretsApi {
  /**
   * @param client Client HTTP verso il gateway.
   */
  constructor(private readonly client: ApiClient) {}

  /**
   * Inserisce o sostituisce una credenziale.
   *
   * Sostituire revoca le consegne già programmate su quella vecchia.
   *
   * @param secret Proprietario, scopo, natura e valore.
   * @returns I metadati della credenziale salvata; mai il valore.
   */
  putSecret(secret: NewSecret): Promise<SecretMetadata> {
    return this.client.request<SecretMetadata>('PUT', '/api/v1/secrets', secret);
  }

  /**
   * Salva la credenziale di un harness per un set.
   *
   * @param setId Set proprietario.
   * @param harness Harness a cui serve.
   * @param kind Natura della credenziale.
   * @param value Valore in chiaro.
   * @returns I metadati della credenziale salvata.
   */
  putSetCredential(
    setId: string,
    harness: HarnessId,
    kind: CredentialKind,
    value: string,
  ): Promise<SecretMetadata> {
    return this.putSecret({
      owner_type: 'agent_set',
      owner_id: setId,
      purpose: harnessPurpose(harness),
      kind,
      value,
    });
  }

  /**
   * Toglie la credenziale propria di un harness del set, per tornare a quella
   * di institor (ADR 0023 §4). Senza credenziale propria non fa nulla.
   *
   * @param setId Set proprietario.
   * @param harness Harness.
   * @returns True se una credenziale è stata tolta.
   */
  async removeSetCredential(setId: string, harness: HarnessId): Promise<boolean> {
    const query = new URLSearchParams({ owner_type: 'agent_set', owner_id: setId });
    const items = await this.client.request<SecretMetadata[]>(
      'GET',
      `/api/v1/secrets?${query.toString()}`,
    );
    const found = items.find((item) => item.purpose === harnessPurpose(harness));
    if (found === undefined) {
      return false;
    }
    await this.client.request<void>('DELETE', `/api/v1/secrets/${encodeURIComponent(found.id)}`);
    return true;
  }
}
